import type { NotificationChangedEvent } from "@ark/contracts";
import { useEffect } from "react";

import {
  desktopRouteFromURL,
  hasNativeDesktopBridge,
  isDesktopRuntime,
} from "./desktopRuntime";
import { sessionManager, useSession } from "./session";

interface DesktopRuntimeBridgeProps {
  navigate: (to: string) => void;
}

const maxDeliveredNotifications = 1024;
const deliveredNotifications = new Map<string, true>();

function rememberNotification(key: string): boolean {
  if (deliveredNotifications.has(key)) return false;
  deliveredNotifications.set(key, true);
  if (deliveredNotifications.size > maxDeliveredNotifications) {
    const oldest = deliveredNotifications.keys().next().value;
    if (oldest) deliveredNotifications.delete(oldest);
  }
  return true;
}

async function sendDesktopNotification(title: string, body: string) {
  const { isPermissionGranted, requestPermission, sendNotification } =
    await import("@tauri-apps/plugin-notification");
  const granted =
    (await isPermissionGranted()) || (await requestPermission()) === "granted";
  if (granted) {
    sendNotification({ body, title });
  }
}

export function DesktopRuntimeBridge({ navigate }: DesktopRuntimeBridgeProps) {
  const session = useSession();

  useEffect(() => {
    if (!isDesktopRuntime()) return;
    document.documentElement.dataset.runtime = "desktop";
    const previousTitle = document.title;
    document.title = "Ark";

    if (!hasNativeDesktopBridge()) {
      return () => {
        document.title = previousTitle;
        delete document.documentElement.dataset.runtime;
      };
    }

    let active = true;
    let unlisten: (() => void) | undefined;

    void import("@tauri-apps/plugin-deep-link")
      .then(async ({ getCurrent, onOpenUrl }) => {
        const open = (urls: string[]) => {
          if (!active) return;
          for (const rawURL of urls) {
            const route = desktopRouteFromURL(rawURL);
            if (route) {
              navigate(route);
              break;
            }
          }
        };
        const stop = await onOpenUrl(open);
        if (!active) {
          stop();
          return;
        }
        unlisten = stop;
        open((await getCurrent()) ?? []);
      })
      .catch((cause: unknown) => {
        console.error("desktop deep-link initialization failed", cause);
      });

    return () => {
      active = false;
      unlisten?.();
      document.title = previousTitle;
      delete document.documentElement.dataset.runtime;
    };
  }, [navigate]);

  useEffect(() => {
    // 原生通知取决于实际 Tauri bridge，而不是 Vite 启动 mode。这样独立启动
    // 普通前端时仍能通知，同时不会启用 desktop 专属菜单/资源排序策略。
    if (!hasNativeDesktopBridge() || !session) return;
    const notifications = sessionManager.notifications.bind();
    const handleEvent = (event: NotificationChangedEvent) => {
      const eventKey = event.notificationId || event.dedupeKey;
      if (
        !eventKey ||
        (document.visibilityState === "visible" && document.hasFocus()) ||
        !rememberNotification(eventKey)
      ) {
        return;
      }

      // transient 通知不落库，SSE 事件会直接携带展示内容；持久通知也优先
      // 使用事件内容，兼容旧服务端时才回查未读列表。
      if (event.title) {
        void sendDesktopNotification(event.title, event.body ?? "").catch(
          () => {
            deliveredNotifications.delete(eventKey);
          },
        );
        return;
      }
      if (!event.notificationId) {
        deliveredNotifications.delete(eventKey);
        return;
      }
      void notifications
        .list({ pageSize: 20, unreadOnly: true })
        .then(({ items }) =>
          items.find((item) => item.id === event.notificationId),
        )
        .then(async (item) => {
          if (!item) {
            deliveredNotifications.delete(eventKey);
            return;
          }
          await sendDesktopNotification(item.title, item.body);
        })
        .catch(() => {
          deliveredNotifications.delete(eventKey);
        });
    };
    // 全局订阅只接收 global/both；IM 消息属于 channel_only，必须显式订阅。
    const stopGlobal = notifications.subscribe(handleEvent);
    const stopIM = sessionManager.notifications
      .bind("system.im")
      .subscribe(handleEvent);
    return () => {
      stopGlobal();
      stopIM();
    };
  }, [session]);

  return null;
}
